<script setup lang="ts">
/*
 * Per-column filter popover — the `<th>` filter menu the ARCH-2
 * prototype attaches to each Directory column (`dirCol*` fns:
 * `toggleDirColMenu` / `dirColMenuHtml` / `dirColMatch` / `clearDirCol`;
 * `.dir-filter-icon` / `.dircol-*` CSS ~L4632–4645, ported into
 * `src/app-elements.css`).
 *
 * SLICE 1 — styled, self-contained, but **not wired into any table**.
 * It is a controlled multi-select popover: pass the column's `options`
 * + the currently-selected values via `v-model`; it emits `update`
 * on every toggle and renders the proto's checkbox-list chrome. The
 * Directory header integration (URL-query sync, badge counts, the
 * type-filter text variant) lands in the Directory overhaul slice.
 *
 * Built on Headless UI's `Popover` (the locked primitive set — D5) so
 * focus trapping + outside-click dismissal come for free; the trigger
 * is the proto's `.dir-filter-icon`.
 */
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import { Filter } from 'lucide-vue-next';
import { computed } from 'vue';

export interface ColumnFilterOption {
  /** Stable value persisted to the filter state / URL query. */
  value: string;
  /** Human-readable label shown in the option row. */
  label: string;
}

const props = withDefaults(
  defineProps<{
    /** Column identifier — surfaced on the trigger + emitted with changes. */
    columnId: string;
    /** Column label, used for the popover heading + a11y. */
    columnLabel: string;
    /** Selectable values for this column. */
    options: ColumnFilterOption[];
    /** Currently-selected values (controlled). */
    modelValue?: string[];
  }>(),
  { modelValue: () => [] },
);

const emit = defineEmits<{
  'update:modelValue': [values: string[]];
  /** Convenience event mirroring the proto's per-column change hook. */
  change: [payload: { columnId: string; values: string[] }];
}>();

const selected = computed(() => props.modelValue ?? []);
const activeCount = computed(() => selected.value.length);

function isChecked(value: string): boolean {
  return selected.value.includes(value);
}

function toggle(value: string): void {
  const next = isChecked(value)
    ? selected.value.filter((v) => v !== value)
    : [...selected.value, value];
  emit('update:modelValue', next);
  emit('change', { columnId: props.columnId, values: next });
}

function clear(): void {
  if (activeCount.value === 0) return;
  emit('update:modelValue', []);
  emit('change', { columnId: props.columnId, values: [] });
}
</script>

<template>
  <Popover class="relative inline-flex" :data-testid="`column-filter-${columnId}`">
    <PopoverButton
      type="button"
      :class="['dir-filter-icon', { 'is-active': activeCount > 0 }]"
      :aria-label="`Filter ${columnLabel}`"
      :title="`Filter ${columnLabel}`"
    >
      <Filter class="h-full w-full" aria-hidden="true" />
    </PopoverButton>

    <PopoverPanel
      v-slot="{ close }"
      class="dircol-menu absolute top-full right-0 z-[61] mt-1"
      :data-testid="`column-filter-panel-${columnId}`"
    >
      <div class="dircol-menu-hd">
        <span>{{ columnLabel }}</span>
        <a
          v-if="activeCount > 0"
          role="button"
          tabindex="0"
          @click="clear()"
          @keydown.enter.prevent="clear()"
          >Clear</a
        >
      </div>
      <div v-if="options.length === 0" class="dircol-empty">No options</div>
      <label
        v-for="opt in options"
        :key="opt.value"
        class="dircol-opt"
        :data-testid="`column-filter-opt-${columnId}-${opt.value}`"
      >
        <input
          type="checkbox"
          class="h-3.5 w-3.5 cursor-pointer rounded-sm border-app-border accent-primary"
          :checked="isChecked(opt.value)"
          @change="toggle(opt.value)"
        />
        <span>{{ opt.label }}</span>
      </label>
      <!-- `close` exposed for callers that want an explicit "Done"; kept
           referenced so the slot binding type-checks without lint noise. -->
      <span class="sr-only" aria-hidden="true" @click="close()" />
    </PopoverPanel>
  </Popover>
</template>
